/* eslint-disable no-restricted-imports */
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@material-ui/core';
import { ArrowBack as ArrowBackIcon, ArrowForward as ArrowForwardIcon } from '@material-ui/icons';

import { ORDER_STEPS } from 'components/Contigo';
import { contigoService } from 'services';

import CustomerSelectionStep from './components/CustomerSelectionStep';
import PricingStep from './components/PricingStep';
import ProductSelectionStep from './components/ProductSelectionStep';
import ReviewStep from './components/ReviewStep';
import { useStyles } from './styles';

export default function CreateOrder() {
  const classes = useStyles();
  const history = useHistory();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const [orderData, setOrderData] = useState({
    order_number: '',
    customer: null,
    items: [],
    notes: '',
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleCancel = () => {
    history.push('/contigo/orders');
  };

  const handleSelectCustomer = (customer) => {
    setOrderData((prev) => ({ ...prev, customer }));
  };

  const handleAddProduct = () => {
    if (!selectedProduct || productQuantity <= 0) return;

    const existingItem = orderData.items.find(
      (item) => item.part_number === selectedProduct.part_number,
    );

    if (existingItem) {
      setOrderData((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.part_number === selectedProduct.part_number
            ? { ...item, quantity: item.quantity + productQuantity }
            : item,
        ),
      }));
    } else {
      setOrderData((prev) => ({
        ...prev,
        items: [
          ...prev.items,
          {
            part_number: selectedProduct.part_number,
            description: selectedProduct.description,
            quantity: productQuantity,
            available_quantity: selectedProduct.available_quantity,
            cost_price: selectedProduct.cost_price || 0,
            sell_price: selectedProduct.sell_price || 0,
          },
        ],
      }));
    }

    setSelectedProduct(null);
    setProductQuantity(1);
  };

  const handleRemoveProduct = (partNumber) => {
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.part_number !== partNumber),
    }));
  };

  const handleUpdatePrice = (partNumber, field, value) => {
    const numValue = parseFloat(value) || 0;
    setOrderData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.part_number === partNumber ? { ...item, [field]: numValue } : item,
      ),
    }));
  };

  const handleCreateOrder = async () => {
    try {
      setLoading(true);

      const payload = {
        order_number: orderData.order_number || undefined,
        customer_id: orderData.customer?.CODIGO,
        items: orderData.items.map((item) => ({
          part_number: item.part_number,
          description: item.description,
          quantity: item.quantity,
          cost_price: parseFloat(item.cost_price),
          sell_price: parseFloat(item.sell_price),
        })),
        notes: orderData.notes || '',
      };
      console.log('payload', payload);
      const response = await contigoService.createOrder(payload);
      console.log('Pedido criado:', response.data);

      const orderId = response.data?.id || response.data?.order?.id;
      const orderNumber = response.data?.order_number || response.data?.order?.order_number;

      toast.success(`Pedido ${orderNumber || 'criado'} com sucesso!`);
      history.push(orderId ? `/contigo/orders/${orderId}` : '/contigo/orders');
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      toast.error(error.response?.data?.message || 'Erro ao criar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <CustomerSelectionStep
            orderNumber={orderData.order_number}
            onOrderNumberChange={(value) =>
              setOrderData((prev) => ({ ...prev, order_number: value }))
            }
            selectedCustomer={orderData.customer}
            onCustomerChange={handleSelectCustomer}
          />
        );

      case 1:
        return (
          <ProductSelectionStep
            selectedProduct={selectedProduct}
            onProductSelect={setSelectedProduct}
            productQuantity={productQuantity}
            onQuantityChange={setProductQuantity}
            onAddProduct={handleAddProduct}
            orderItems={orderData.items}
            onRemoveProduct={handleRemoveProduct}
          />
        );

      case 2:
        return <PricingStep orderItems={orderData.items} onUpdatePrice={handleUpdatePrice} />;

      case 3:
        return (
          <ReviewStep
            customer={orderData.customer}
            orderItems={orderData.items}
            notes={orderData.notes}
            onNotesChange={(value) => setOrderData((prev) => ({ ...prev, notes: value }))}
          />
        );

      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return orderData.customer !== null;
      case 1:
        return orderData.items.length > 0;
      case 2:
        return orderData.items.every((item) => item.cost_price > 0 && item.sell_price > 0);
      case 3:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box className={classes.container}>
      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.header}>
            <Typography variant='h5' className={classes.title}>
              Criar Novo Pedido
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} className={classes.stepper}>
            {ORDER_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box className={classes.stepContent}>{renderStepContent(activeStep)}</Box>

          <Divider className={classes.divider} />

          <Box className={classes.actions}>
            <Button onClick={handleCancel} className={classes.cancelButton}>
              Cancelar
            </Button>
            <Box className={classes.navigationButtons}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
              >
                Voltar
              </Button>
              {activeStep === ORDER_STEPS.length - 1 ? (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleCreateOrder}
                  disabled={loading || !isStepValid()}
                  className={classes.finishButton}
                >
                  {loading ? 'Criando...' : 'Finalizar Pedido'}
                </Button>
              ) : (
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  disabled={!isStepValid()}
                >
                  Próximo
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Backdrop open={loading} style={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </Box>
  );
}
